import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useLazyQuery } from "@apollo/client";
import { intl } from "LocaleWrapper";

import { Button, Card, PageHeader } from "@cofano/cck";
import { Box, CircularProgress, Grid2 as Grid, Tab, Tabs } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";

import Helmet from "components/helmet/Helmet";
import useTabs from "components/hooks/useTabs";
import { ProductStatusTag, ProductValidationStatusTag } from "components/products/ProductTags";

import { Language, usePermission } from "imports/Utils";

import ProductGeneral from "view/products/components/details/ProductGeneral";
import ProductDangerousGoods from "view/products/components/details/ProductDangerousGoods";
import ProductNstrDetails from "view/products/components/details/ProductNstrDetails";
import ProductIdtfDetails from "view/products/components/details/ProductIdtfDetails";
import ProductDocuments from "view/products/components/details/ProductDocuments";

import { GetProductDocument } from "__generated__/view/products/graphql/product.queries.generated";

import { ProductTabs } from "./utils/ProductUtils";
import { Product } from "./utils/product.types";

const ProductDetails = () => {

	const navigate = useNavigate();
	const { id } = useParams();

	const { isReadOnly, isAdmin, isMasterdata, isCompanyUser, isMainUserLimited, isRTR } = usePermission();
	const canEdit = !isReadOnly && (isAdmin || isMasterdata || isCompanyUser || isMainUserLimited);

	const [ getProduct, { data, loading, refetch } ] = useLazyQuery(GetProductDocument, {
		variables: {
			id: Number(id),
		},
	});

	const product = data?.product || null;

	useEffect(() => {
		if (id) {
			getProduct();
		}
	}, []);

	return (
		<div>
			<PageHeader>
				<PageHeader.Title>
					{product?.description || Language("PRODUCT")}
					<Helmet title={product?.name || product?.code || intl.formatMessage({ id: "PRODUCT" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					{isRTR && (
						<Button onClick={() => navigate("/view/products")} variant="outlined">
							{Language("PRODUCTS")}
						</Button>
					)}

					{canEdit && (
						<Button onClick={() => navigate(`/view/products/${id}/edit${window.location.search}`)}>
							{Language("EDIT")}
						</Button>
					)}
				</PageHeader.Actions>
			</PageHeader>

			{(product && !loading) ? (
				<Page product={product} refetch={refetch} />
			) : (
				<CircularProgress size={24} />
			)}
		</div>
	);
};

const Page = ({ product, refetch }: { product: Product, refetch: () => void }) => {

	const defaultSelectedTab =
		product.isDangerousGood
			? ProductTabs.DangerousGoods
			: product.nstrGood
				? ProductTabs.Nstr
				: product.idtfGood
					? ProductTabs.Idtf
					: ProductTabs.Documents;


	const { selectedTab, selectTab } = useTabs<ProductTabs>({
		defaultSelected: defaultSelectedTab,
	});

	return (
		<Grid container spacing={3}>
			<Grid size={{ lg: 5, md: 12, xs: 12 }}>
				<Card
					title={Language("DETAILS")}
					icon={<InfoIcon />}
					actions={[ {
						render: () => (
							<Box mr={0.5}>
								<ProductStatusTag
									product={product}
									onChange={refetch} />
							</Box>
						),
					}, {
						render: () => (
							<ProductValidationStatusTag
								product={product}
								onChange={refetch} />
						),
					} ]}>
					<ProductGeneral product={product} />
				</Card>
			</Grid>

			<Grid size={{ lg: 7, md: 12, xs: 12 }}>
				<Card disablePadding>
					<Box sx={{ borderBottom: 1, borderColor: "divider" }}>
						<Tabs value={selectedTab} onChange={(_, value) => selectTab(value)}>
							<Tab label={intl.formatMessage({ id: "CARGO_DANGEROUS_GOOD" })} value={ProductTabs.DangerousGoods} disabled={!product.isDangerousGood} />
							<Tab label={intl.formatMessage({ id: "PRODUCTS_NSTR" })} value={ProductTabs.Nstr} disabled={!product.nstrGood} />
							<Tab label={intl.formatMessage({ id: "PRODUCTS_IDTF" })} value={ProductTabs.Idtf} disabled={!product.idtfGood} />
							<Tab label={intl.formatMessage({ id: "DOCUMENTS" })} value={ProductTabs.Documents} />
						</Tabs>
					</Box>

					<Box p={2}>
						{{
							[ProductTabs.DangerousGoods]: <ProductDangerousGoods product={product} refetch={refetch} />,
							[ProductTabs.Nstr]: <ProductNstrDetails nstrGood={product.nstrGood} />,
							[ProductTabs.Idtf]: <ProductIdtfDetails idtfGood={product.idtfGood} />,
							[ProductTabs.Documents]: <ProductDocuments product={product} />,
						}[selectedTab]}
					</Box>
				</Card>
			</Grid>
		</Grid>
	);
};

export default ProductDetails;
